import configuration from '../configuration';
import BaseAPI from './BaseAPI';

/**
 * One node of a filter tree sent to analytics-service: either a group node (op + clauses) or a
 * leaf node (field + operator + values). The service validates field/operator against its own
 * whitelist (senderAccountId, recipientAccountId, hasAttachment, messageType; EQ, IN) — org scoping
 * is never expressible here, since the service always applies it itself. senderAccountId/
 * recipientAccountId each match against both a user account id and a role account id server-side,
 * so a single id from the typeahead endpoint (searchUsersAndRoles) works whether it's a user or role.
 */
export type ArchivingFilterNode = {
  op?: 'AND' | 'OR';
  clauses?: ArchivingFilterNode[];
  field?: 'senderAccountId' | 'recipientAccountId' | 'hasAttachment' | 'messageType';
  operator?: 'EQ' | 'IN';
  values?: string[];
};

export type ArchivingMessageSearchRequest = {
  orgId: string;
  query: string;
  startDate?: string;
  endDate?: string;
  hasAttachment?: boolean;
  senderAccountIds?: string[];
  recipientAccountIds?: string[];
  filter?: ArchivingFilterNode;
  limit?: number;
  columns?: string[];
};

/**
 * Known fields on a search result row — kept optional/loose beyond this since the exact backend
 * column set is still a placeholder pending data eng confirmation (see
 * analytics-service/docs/ARCHIVING_MESSAGE_SEARCH.md's "Known placeholders" notes).
 * senderAccountId/recipientAccountId are the raw account tokens (recipientAccountId is null for a
 * group/DL result — see analytics-service's DISTINCT dedupe); senderDisplayName/
 * recipientDisplayName are the resolved names (ACCOUNTS.DISPLAYNAME, falling back to
 * "First Last", falling back to the raw id) — "Role Name (User Name)" when a message has both a
 * role and a user sender/recipient. recipientDisplayName is null for group/DL, same as
 * recipientAccountId. channelId is null for a p2p result, populated for group/DL.
 */
export type ArchivingMessageSearchResult = {
  messageId?: string;
  sentAt?: string;
  senderAccountId?: string | null;
  senderDisplayName?: string;
  recipientAccountId?: string | null;
  recipientDisplayName?: string | null;
  body?: string;
  hasAttachment?: boolean;
  /** Raw MESSAGES.MESSAGETYPE value (e.g. P2P, GROUP, DIST, PUBLIC_GROUP, DIST_GROUP), or null when unset. */
  messageType?: string | null;
  conversationId?: string;
  channelId?: string | null;
};

export type ArchivingMessageSearchData = {
  results: ArchivingMessageSearchResult[];
  resultCount: number;
};

export type ArchivingMessageSearchMetadata = {
  processingTimeMs?: number;
  snowflakeAccount?: string;
};

/** status/requestId/timestamp envelope shared by every Archiving response type. */
export type ArchivingResponseEnvelope = {
  status: string;
  requestId: string;
  timestamp: string;
};

export type ArchivingMessageSearchResponse = ArchivingResponseEnvelope & {
  data: ArchivingMessageSearchData;
  metadata?: ArchivingMessageSearchMetadata;
};

export type ArchivingConversationRequest = {
  orgId: string;
  conversationId: string;
  limit?: number;
};

/**
 * Known fields on a conversation message — same placeholder caveat as ArchivingMessageSearchResult.
 * No recipientAccountId/recipientDisplayName here (unlike search): the conversation endpoint omits
 * them entirely rather than nulling them, since this view shows the thread rather than who
 * received what. senderDisplayName resolution matches ArchivingMessageSearchResult's.
 */
export type ArchivingConversationMessage = {
  messageId?: string;
  sentAt?: string;
  senderAccountId?: string | null;
  senderDisplayName?: string;
  body?: string;
  hasAttachment?: boolean;
  conversationId?: string;
  channelId?: string | null;
};

export type ArchivingConversationData = {
  messages: ArchivingConversationMessage[];
};

export type ArchivingConversationResponse = ArchivingResponseEnvelope & {
  data: ArchivingConversationData;
  metadata?: ArchivingMessageSearchMetadata;
};

export type ArchivingConversationMetadataRequest = {
  orgId: string;
  conversationId: string;
};

export type ArchivingConversationMetadataData = {
  conversationId: string;
  channelId: string | null;
  messageCount: number;
};

export type ArchivingConversationMetadataResponse = ArchivingResponseEnvelope & {
  data: ArchivingConversationMetadataData;
  metadata?: ArchivingMessageSearchMetadata;
};

export type ArchivingExportRequest = {
  orgId: string;
  exportName?: string;
  /**
   * Message identifiers to export. The caller resolves all product use cases (selected messages, a
   * full conversation thread, a saved search) into this one list before calling startExport, so the
   * export always reflects exactly what the user reviewed rather than a live re-query at export time.
   */
  messageIds: string[];
  includeAttachments?: boolean;
  notes?: string;
};

export type ArchivingExportData = {
  exportJobId: string;
  /** Job lifecycle status. Always 'queued' for now — no job processing exists yet. */
  jobStatus: string;
};

export type ArchivingExportResponse = ArchivingResponseEnvelope & {
  data: ArchivingExportData;
};

export type ArchivingExportListRequest = {
  orgId: string;
};

export type ArchivingExportSummary = {
  exportJobId: string;
  exportName?: string;
  /** 'queued' | 'processing' | 'ready' | 'failed' */
  jobStatus: string;
  createdAt: string;
  /**
   * When this job's TTL semantics say it should be considered gone — recomputed on every status
   * transition server-side (queued/processing/ready/failed each have their own window). Check this
   * before treating a 'ready' job as downloadable; analytics-service's download endpoint enforces
   * it too, but the UI shouldn't rely solely on that round trip to decide what to show as available.
   */
  expiresAt: string;
};

export type ArchivingExportListData = {
  /** The caller's own export jobs, most recent first — never every export in the org (no archiving-admin role yet). */
  exports: ArchivingExportSummary[];
};

export type ArchivingExportListResponse = ArchivingResponseEnvelope & {
  data: ArchivingExportListData;
};

export type ArchivingExportDownloadRequest = {
  orgId: string;
  jobId: string;
};

export type ArchivingExportDownloadData = {
  downloadUrl: string;
  /** When this presigned URL itself expires (15 minutes from issuance) — not the export job's own expiresAt. */
  urlExpiresAt: string;
};

export type ArchivingExportDownloadResponse = ArchivingResponseEnvelope & {
  data: ArchivingExportDownloadData;
};

export type ArchivingTypeaheadRequest = {
  orgId: string;
  /** Name text to search for, matched anywhere in the name (minimum 2 characters). */
  query: string;
  limit?: number;
};

export type ArchivingTypeaheadResult = {
  /** User or role account token — the same id accepted by ArchivingFilterNode's senderAccountId/recipientAccountId fields. */
  id: string;
  displayName: string;
  type: 'user' | 'role';
};

export type ArchivingTypeaheadData = {
  results: ArchivingTypeaheadResult[];
};

export type ArchivingTypeaheadResponse = ArchivingResponseEnvelope & {
  data: ArchivingTypeaheadData;
  metadata?: ArchivingMessageSearchMetadata;
};

/**
 * One selected user/role row from a saved search's filters. Same shape as ArchivingTypeaheadResult
 * plus which selector (sender/recipient) the row was entered under — persisted verbatim so a saved
 * search restores its user/role rows without a follow-up typeahead lookup.
 */
export type ArchivingSavedSearchUserRole = {
  id: string;
  displayName: string;
  type: 'user' | 'role';
  role: 'sender' | 'recipient' | 'either';
};

/** The full Message Search filter state, as persisted by a saved search. */
export type ArchivingSavedSearchFilters = {
  query?: string;
  /** 'last24h' | 'last7d' | 'last30d' | 'thisQuarter' | 'custom' */
  timeRangePreset?: string;
  /** Only meaningful when timeRangePreset is 'custom'. */
  customStartDate?: string;
  /** Only meaningful when timeRangePreset is 'custom'. */
  customEndDate?: string;
  userRoles?: ArchivingSavedSearchUserRole[];
  /** 'any' | 'all' — how multiple userRoles rows are combined (OR vs AND). Only meaningful with 2+ rows. */
  userRoleMatchMode?: 'any' | 'all';
  messageType?: string | null;
  patientReference?: string | null;
  hasAttachment?: boolean | null;
};

export type ArchivingCreateSavedSearchRequest = {
  orgId: string;
  name: string;
  filters: ArchivingSavedSearchFilters;
};

export type ArchivingSavedSearchData = {
  searchId: string;
  name: string;
  filters: ArchivingSavedSearchFilters;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
};

export type ArchivingSavedSearchResponse = ArchivingResponseEnvelope & {
  data: ArchivingSavedSearchData;
};

export type ArchivingSavedSearchListRequest = {
  orgId: string;
  /** When true, scopes to the caller's own saved searches. Defaults to false (every saved search in the org) server-side. */
  mine?: boolean;
};

export type ArchivingSavedSearchListData = {
  /** Saved searches, most recently created first. */
  savedSearches: ArchivingSavedSearchData[];
};

export type ArchivingSavedSearchListResponse = ArchivingResponseEnvelope & {
  data: ArchivingSavedSearchListData;
};

export type ArchivingDeleteSavedSearchRequest = {
  orgId: string;
  searchId: string;
};

/** No data payload on a successful delete — just the shared envelope. */
export type ArchivingDeleteSavedSearchResponse = ArchivingResponseEnvelope;

export default class ArchivingAPI extends BaseAPI {
  // analytics-service lives at api.{env}.tigerconnect.com, not the devapi base URL
  private get tcBaseUrl(): string {
    const { apiEnv } = this.config;
    return configuration.TC_URLS[apiEnv] || `https://api.${apiEnv}.tigerconnect.com`;
  }

  searchMessages = async (
    organizationId: string,
    payload: ArchivingMessageSearchRequest
  ): Promise<ArchivingMessageSearchResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/search`;
    const { data } = await this.httpClient.post(url, {
      headers,
      data: payload,
    });
    return data as unknown as ArchivingMessageSearchResponse;
  };

  getConversation = async (
    organizationId: string,
    payload: ArchivingConversationRequest
  ): Promise<ArchivingConversationResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/conversation`;
    const { data } = await this.httpClient.post(url, {
      headers,
      data: payload,
    });
    return data as unknown as ArchivingConversationResponse;
  };

  getConversationMetadata = async (
    organizationId: string,
    payload: ArchivingConversationMetadataRequest
  ): Promise<ArchivingConversationMetadataResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/conversation/metadata`;
    const { data } = await this.httpClient.post(url, {
      headers,
      data: payload,
    });
    return data as unknown as ArchivingConversationMetadataResponse;
  };

  startExport = async (
    organizationId: string,
    payload: ArchivingExportRequest
  ): Promise<ArchivingExportResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/export`;
    const { data } = await this.httpClient.post(url, {
      headers,
      data: payload,
    });
    return data as unknown as ArchivingExportResponse;
  };

  listExports = async (
    organizationId: string,
    payload: ArchivingExportListRequest
  ): Promise<ArchivingExportListResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/export`;
    const { data } = await this.httpClient.get(url, {
      headers,
      query: { orgId: payload.orgId },
    });
    return data as unknown as ArchivingExportListResponse;
  };

  getExportDownloadUrl = async (
    organizationId: string,
    payload: ArchivingExportDownloadRequest
  ): Promise<ArchivingExportDownloadResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/export/${payload.jobId}/download`;
    const { data } = await this.httpClient.get(url, {
      headers,
      query: { orgId: payload.orgId },
    });
    return data as unknown as ArchivingExportDownloadResponse;
  };

  searchUsersAndRoles = async (
    organizationId: string,
    payload: ArchivingTypeaheadRequest
  ): Promise<ArchivingTypeaheadResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/typeahead`;
    const { data } = await this.httpClient.get(url, {
      headers,
      query: {
        orgId: payload.orgId,
        query: payload.query,
        ...(payload.limit !== undefined ? { limit: payload.limit } : {}),
      },
    });
    return data as unknown as ArchivingTypeaheadResponse;
  };

  createSavedSearch = async (
    organizationId: string,
    payload: ArchivingCreateSavedSearchRequest
  ): Promise<ArchivingSavedSearchResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/saved-searches`;
    const { data } = await this.httpClient.post(url, {
      headers,
      data: payload,
    });
    return data as unknown as ArchivingSavedSearchResponse;
  };

  getSavedSearches = async (
    organizationId: string,
    payload: ArchivingSavedSearchListRequest
  ): Promise<ArchivingSavedSearchListResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/saved-searches`;
    const { data } = await this.httpClient.get(url, {
      headers,
      query: {
        orgId: payload.orgId,
        ...(payload.mine !== undefined ? { mine: payload.mine } : {}),
      },
    });
    return data as unknown as ArchivingSavedSearchListResponse;
  };

  deleteSavedSearch = async (
    organizationId: string,
    payload: ArchivingDeleteSavedSearchRequest
  ): Promise<ArchivingDeleteSavedSearchResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/saved-searches/${payload.searchId}`;
    const { data } = await this.httpClient.del(url, {
      headers,
      query: { orgId: payload.orgId },
    });
    return data as unknown as ArchivingDeleteSavedSearchResponse;
  };
}
